import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import React, { useEffect, useState } from 'react'
import __order from './__order'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import { OrderProducts } from './modules/OrderProducts'
import {
    Row,
    Col,
    Modal,
    Tag,
    Table,
    Button,
    Space,
    Select,
    Divider,
    Input,
    InputNumber,
    DatePicker,
    Alert,
    message,
    Tooltip
} from 'antd'
import {
    ShoppingOutlined, CarOutlined, GiftOutlined, TeamOutlined, WalletOutlined, RollbackOutlined,
    CopyOutlined, ClockCircleOutlined, CreditCardOutlined, SyncOutlined, DollarCircleOutlined, NumberOutlined,
    SafetyCertificateOutlined
} from '@ant-design/icons'
import { storeLocal } from "../../../helpers/stores";
import { MapLocation } from '../../../components/map/MapLocation'
import { runInAction } from 'mobx'
import { paymentTypesController } from '../payment/types/controller'
import { getPrice } from '../../../helpers/price'
import dayjs from 'dayjs'
import __delivery from '../../settings/delivery/base/__delivery'
import type { InputNumberProps } from 'antd'
import { AdditionalFields } from "../../../components/AdditionalFields";
import projectController from "../../settings/project/project.controller";
import { ProductTable } from '../../../components/product_table/ProductTable'
import { ProductTableController } from '../../../components/product_table/product_table.controller'

const payments = new paymentTypesController()

function secondsToHHii(seconds: any) {
    let hours = Math.floor(seconds / 3600);
    let minutes = Math.floor((seconds % 3600) / 60);
    return `${padZero(hours)}:${padZero(minutes)}`;
}

function padZero(num: any) {
    return (num < 10 ? '0' : '') + num;
}

// уже показаны отдельными строками ниже (item.cashback / item.size_bonus_points) — не дублируем их в плашках
const PRODUCTS_DISCOUNTS_SUMMARY_EXCLUDED_TYPES = ['cashback', 'bonus_writeoff']

const getProductsDiscountsSummary = (products: any[]) => {
    let sum = 0
    const discountsMap: Record<string, { label: string, amount: number }> = {}

    ;(products ?? []).filter((item: any) => item.product).forEach((item: any) => {
        const count = item.count ?? 0

        sum += (item.price ?? 0) * count

        ;(item.discounts ?? [])
            .filter((discount: any) => !PRODUCTS_DISCOUNTS_SUMMARY_EXCLUDED_TYPES.includes(discount.type))
            .forEach((discount: any) => {
                const key = discount.label ?? discount.type

                if(!discountsMap[key]) discountsMap[key] = { label: key, amount: 0 }
                discountsMap[key].amount += discount.amount
            })
    })

    const discounts = Object.values(discountsMap).sort((a, b) => (a.amount > 0 ? 1 : 0) - (b.amount > 0 ? 1 : 0))

    return { sum, discounts }
}

const CostLine = ({ icon, label, sub, value, valueColor }: {
    icon?: React.ReactNode
    label: React.ReactNode
    sub?: React.ReactNode
    value: React.ReactNode
    valueColor?: string
}) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '8px 0' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
            {icon &&
              <div style={{
                  width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(114,100,214,0.1)', color: 'var(--primary-color)', fontSize: 14
              }}>
                  {icon}
              </div>
            }
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-color)', lineHeight: 1.3 }}>{label}</div>
                {sub}
            </div>
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: valueColor ?? 'var(--text-color)', whiteSpace: 'nowrap', paddingTop: icon ? 6 : 0 }}>{value}</div>
    </div>
)

const FieldCard = ({ icon, label, children, full }: {
    icon: React.ReactNode
    label: React.ReactNode
    children: React.ReactNode
    full?: boolean
}) => (
    <div style={{
        flex: full ? '1 1 100%' : '1 1 200px',
        background: 'var(--surface-field-color)', borderRadius: 12, padding: '10px 14px',
        display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
                width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(114,100,214,0.12)', color: 'var(--primary-color)', fontSize: 12
            }}>{icon}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary-color)' }}>{label}</span>
        </div>
        <div>{children}</div>
    </div>
)

// Свойства товара без вариаций выбираются прямо в строке, до добавления в заказ
// (addProduct берёт выбранное из record.props)
const ProductPropsSelect = ({ product }: { product: any }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {product.props.map((el: any, index: number) => (
            <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary-color)' }}>{el.category?.name}</label>
                <Select
                    size="small"
                    style={{ width: 220 }}
                    options={el.options}
                    onChange={(value: any) => runInAction(() => {
                        const option = el.options.find((f: any) => f.value === value)
                        el.value = option?.label
                        el.label = el.category?.name
                        el.id = value
                    })}
                />
            </div>
        ))}
    </div>
)

const getLogValueText = (value: any) => {
    if (value === null || value === undefined) return '';

    if (typeof value === 'string') return value;

    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

export const Order = observer(() => {

    let { id }: any = useParams()

    // Товары для заказа — та же таблица, что в каталоге. /products/select отдаёт только показанные в каталоге товары
    // с вариациями и их свойствами и ищет по названию, ID и ID CRM — без фильтров и сортировки
    const [productTable] = useState(() => new ProductTableController({
        endpoint: '/products/select',
        searchFields: ['name', 'id', 'id_crm'],
        filters: false,
        sortable: false,
    }))

    useEffect(() => {
        if (id > 0) __order.getOrder(id)
        else __order.setOrder()
        projectController.get()

        __order.getStatuses()
        payments.getList()
        __delivery.get()
    }, [])

    const onChangeModalSum: InputNumberProps['onChange'] = (value) => {
        __order.holding_modal.sum = value ?? 0
        console.log('changed', __order.holding_modal.sum)
    }

    const usersColumns = [
        {
            title: i.t('users:name'),
            key: 'name',
            render: (record: any) => `${record.firstname ?? ''} ${record.lastname ?? ''} ${record.surname ?? ''}`
        },
        {
            title: i.t('users:email'),
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: i.t('users:phone'),
            dataIndex: 'phone',
            key: 'phone'
        },
        {
            title: i.t('points:actions'),
            key: 'action',
            render: (text: any, record: any) => (
                <>
                    <Space>
                        <Button onClick={() => __order.addUser(record)}>{i.t('def:select')}</Button>
                    </Space>
                </>
            ),
        },
    ]

    const buttons: any[] = [
        {
            title: __order.editable ? i.t('def:actions.save') : i.t('def:actions.edit'),
            click: __order.editable ? () => __order.save() : () => __order.setEditable(),
            type: __order.editable ? 'primary' : 'dashed',
            plan: 2
        },
    ]

    if(__order.editable) {
        buttons.unshift({
            title: __order.item.id > 0 ? i.t('def:Cancel Editing') : i.t('def:actions.back'),
            click: () => __order.item.id > 0 ? __order.setEditable() : window.location.href = '/shop/orders',
            type: 'dashed'
        })

        buttons.push({
            title: i.t('def:actions.save_not_recalculation'),
            click: () => __order.save(true, false),
            type: 'primary'
        })
    }

    buttons.push({
        title: __order.item.is_archive ? i.t('def:fromArchive') : i.t('def:toArchive'),
        click: () => __order.item.is_archive ? __order.fromArchive(__order.item.id) : __order.toArchive(__order.item.id),
        type: 'dashed'
    })

    if(__order.item.payment_status === 3 && !__order.item.holding) {
        buttons.push({
            title: i.t('def:holding'),
            click: () => __order.holding(__order.item.id),
            type: 'dashed'
        })

        buttons.push({
            title: i.t('def:un_holding'),
            click: () => __order.un_holding(__order.item.id),
            type: 'dashed'
        })
    }

    if(!__order.item.external_number && __order.item.id > 0) {
        buttons.push({
            title: i.t('order:repeatSendingExternal'),
            click: () => __order.repeatSending(__order.item.id),
            type: 'dashed'
        })
    }

    let breadcrumbs: any[] = [
        { title: i.t('order:orders'), href: '/shop/orders' }
    ]

    if (id > 0) breadcrumbs.push({ title: i.t('def:Order', { id: id }) })
    else breadcrumbs.push({ title: i.t('def:New Order') })

    let pageTitle = id > 0 ? i.t('def:Order', { id: id }) : i.t('def:New Order')

    return (
        <>
            <Titles
                title={pageTitle}
                buttons={buttons}
                breadcrumb={breadcrumbs}
            />

            <Row gutter={[20, 20]}>
                {__order.item.rejection_reason_object ?
                    <Col span={24}>
                        <Alert
                            message={`${i.t('def:rejection_reason')}: ${__order.item.rejection_reason_object.label ?? ''}`}
                            description={__order.item.rejection_reason_comment ? __order.item.rejection_reason_comment : ''}
                            type="error"
                            showIcon
                        />
                    </Col> : <></>
                }

                <Col span={24} xl={12}>
                    <div className="order__block">
                        {__order.item?.code_receive &&
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                width: '100%', padding: '14px 20px', borderRadius: 14,
                                background: 'linear-gradient(135deg, #3f3d56, #63607f)'
                            }}>
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                        {i.t('def:Code for issuing an order')}
                                    </div>
                                    <div style={{ fontFamily: 'monospace', fontSize: 26, fontWeight: 700, color: '#fff', letterSpacing: 2, marginTop: 2 }}>
                                        {__order.item.code_receive}
                                    </div>
                                </div>
                                <Tooltip title={i.t('order:clickToCopy')}>
                                    <Button
                                        shape="circle"
                                        icon={<CopyOutlined />}
                                        onClick={() => {
                                            navigator.clipboard.writeText(`${__order.item.code_receive}`)
                                            message.success(i.t('order:copied'))
                                        }}
                                        style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff' }}
                                    />
                                </Tooltip>
                            </div>
                        }

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, width: '100%' }}>
                            {__order.item?.external_number &&
                                <FieldCard icon={<NumberOutlined />} label={i.t('order:externalNumber')} full>
                                    <div className="item__text">{__order.item.external_number}</div>
                                </FieldCard>
                            }

                            {__order.item?.created_at &&
                                <FieldCard icon={<ClockCircleOutlined />} label={i.t('order:datetime')}>
                                    <div className="item__text">{dayjs(__order.item.created_at).format('DD.MM.YYYY HH:mm')}</div>
                                </FieldCard>
                            }

                            <FieldCard icon={<CreditCardOutlined />} label={i.t('order:type')}>
                                <div className="item__text">
                                    {__order.editable ? (
                                        <>
                                            {__order.item.payment_type_id
                                              ? <Select
                                                options={__delivery.delivery.list_pay_new.map((el: any) => ({
                                                    label: el.label,
                                                    value: el.id
                                                }))}
                                                value={__order.item.payment_type_id}
                                                onChange={(e) => runInAction(() => __order.item.payment_type_id = e)}
                                              />
                                              : <Select
                                                options={__delivery.delivery.list_pay.map((el: any) => ({
                                                    label: el.name,
                                                    value: el.id
                                                }))}
                                                value={__order.item.payment_type}
                                                onChange={(e) => runInAction(() => __order.item.payment_type = e)}
                                              />
                                            }
                                        </>
                                      )
                                        :
                                        <>{__order.item.payment_type_object?.name}</>
                                    }
                                </div>
                            </FieldCard>

                            {Number(__order.item.save_card_id) > 0 &&
                                <FieldCard icon={<SafetyCertificateOutlined />} label={i.t('order:savedCard')}>
                                    <div className="item__text">
                                        {__order.savedCardLabel(__order.item.saved_card) || i.t('order:savedCardAttached')}
                                    </div>
                                    <div style={{ marginTop: 2, fontSize: 12, color: 'var(--text-muted-color)' }}>{i.t('order:savedCardHint')}</div>
                                    {!__order.editable && __order.canChargeSavedCard() &&
                                        <Button
                                            size="small"
                                            style={{ marginTop: 8 }}
                                            loading={__order.chargingCard}
                                            onClick={() => Modal.confirm({
                                                title: i.t('order:savedCardChargeConfirm', { sum: getPrice(__order.item.sum) }),
                                                content: i.t('order:savedCardChargeWarning'),
                                                okText: i.t('order:savedCardCharge'),
                                                cancelText: i.t('order:savedCardChargeCancel'),
                                                onOk: () => __order.chargeSavedCard(__order.item.id)
                                            })}
                                        >
                                            {i.t('order:savedCardCharge')}
                                        </Button>
                                    }
                                </FieldCard>
                            }

                            <FieldCard icon={<SyncOutlined />} label={i.t('order:statusOrder')} full>
                                <Select
                                    style={{ width: '100%' }}
                                    options={__order.statuses.map((el: any) => ({
                                        label: el.name,
                                        value: el.id
                                    }))}
                                    value={__order.item.status}
                                    onChange={(e) => runInAction(() => {
                                        __order.item.status = e

                                        if(!__order.editable) {
                                            __order.save(false)
                                        }
                                    })}
                                />
                            </FieldCard>

                            <FieldCard icon={<DollarCircleOutlined />} label={i.t('order:statusPay')} full>
                                <Select
                                    style={{ width: '100%' }}
                                    options={payments.list.map((el: any) => ({
                                        label: el.name,
                                        value: el.id
                                    }))}
                                    value={__order.item.payment_status}
                                    onChange={(e) => runInAction(() => {
                                        __order.item.payment_status = e

                                        if(!__order.editable) {
                                            __order.save(false)
                                        }
                                    })}
                                    disabled={__order.item.payment_status === 3 && !__order.item.holding}
                                />
                            </FieldCard>
                        </div>
                    </div>
                </Col>

                <Col span={24} xl={12}>
                    <div className="order__block">
                        <div className="order__block-item">
                            <div className="item__heading">{i.t('order:price')}</div>
                            <Row gutter={[20, 20]} align="middle">
                                {(() => {
                                    const summary = getProductsDiscountsSummary(__order.item.products)
                                    const hasOldPrice = __order.item.productsBasePrice !== summary.sum && __order.item.productsBasePrice > 0
                                    const hasCashback = __order.item.cashback > 0 || __order.editable
                                    const hasBonusWriteoff = __order.item.size_bonus_points > 0 || __order.editable

                                    const badges: { key: string, label: string, amount: number, isPoints?: boolean }[] = [
                                        ...summary.discounts.map((discount: any) => ({ key: discount.label, label: discount.label, amount: discount.amount }))
                                    ]

                                    if(hasCashback) badges.push({ key: 'cashback', label: i.t('order:cashback_add'), amount: Math.abs(__order.item.cashback), isPoints: true })
                                    if(hasBonusWriteoff) badges.push({ key: 'bonus_writeoff', label: i.t('order:size_bonus_points'), amount: -Math.abs(__order.item.size_bonus_points), isPoints: true })

                                    badges.sort((a, b) => (a.amount > 0 ? 1 : 0) - (b.amount > 0 ? 1 : 0))

                                    if(!hasOldPrice && badges.length === 0) return <></>

                                    return (
                                        <Col span={24}>
                                            <div className="order__price-breakdown">
                                                {hasOldPrice &&
                                                    <div className="order__price-breakdown-row">
                                                        <span className="item__text">{i.t('order:productsWithoutDiscount')}</span>
                                                        <span className="item__price-old">{getPrice(__order.item.productsBasePrice.toFixed(2))}</span>
                                                    </div>
                                                }

                                                {badges.map((badge) => (
                                                    <div className="order__price-breakdown-row" key={badge.key}>
                                                        <span className="item__text">{badge.label}</span>
                                                        <span className={`item__price-amount ${badge.amount > 0 ? 'item__price-amount--positive' : 'item__price-amount--negative'}`}>
                                                            {badge.amount > 0 ? '+' : ''}{badge.isPoints ? badge.amount : getPrice(badge.amount.toFixed(2))}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </Col>
                                    )
                                })()}

                                {__order.editable &&
                                    <Col span={24}>
                                        <Row gutter={[10, 10]}>
                                            <Col span={24}>
                                                <Alert message={i.t('def:user_balance', { balance: __order.item.user_balance ?? 0 })} type="warning" />
                                            </Col>
                                            <Col span={24}>
                                                <Alert message={i.t('def:user_balance_after_edit', { balance: __order.getUserBalance() })} type="error" />
                                            </Col>
                                        </Row>
                                    </Col>
                                }
                            </Row>
                        </div>

                        <div style={{ background: 'var(--surface-field-color)', borderRadius: 14, padding: '2px 16px', width: '100%' }}>
                            <CostLine
                                icon={<ShoppingOutlined />}
                                label={`${i.t('order:products')} (${__order.item?.products_sum?.count ?? 0})`}
                                value={getPrice((__order.item?.products_sum?.sum ?? 0).toFixed(2))}
                            />

                            {__order.item.delivery_price > 0 &&
                                <CostLine
                                    icon={<CarOutlined />}
                                    label={i.t('order:deliveries')}
                                    value={getPrice((__order.item.delivery_price).toFixed(2))}
                                />
                            }

                            {(() => {
                                // Денежный эффект обычных % / фикс. скидок и купонов уже показан выше,
                                // в сводке "Товары (без скидки)/Автоскидка/Купон" — она строится по
                                // построчным discounts на товарах (orders_ex_product_discounts).
                                // Здесь, в order.discounts (ex_discounts_orders), показываем только то,
                                // чего в той сводке в принципе не может быть — подарочные позиции:
                                // гифт-скидки GiftService (measurement 2/3/4) и купоны с прямым товаром-подарком.
                                const GIFT_DISCOUNT_MEASUREMENTS = [2, 3, 4]
                                const discountGiftProducts = (__order.item.free_products ?? [])
                                  .filter((p: any) => p.product_type === 'discount' && p.product)
                                // Подарки купона берём из самого заказа: одиночное product_id у купона
                                // при сохранении купона обнуляется — подарки теперь в coupon.gifts
                                const couponGiftProducts = (__order.item.free_products ?? [])
                                  .filter((p: any) => p.product_type === 'promocode' && p.product)
                                  .map((p: any) => p.product)

                                return (__order.item.discounts ?? []).map((disc: any, idx: number) => {
                                    if (disc.discount) {
                                        if (!GIFT_DISCOUNT_MEASUREMENTS.includes(disc.measurement)) {
                                            return null
                                        }

                                        return (
                                            <CostLine
                                                key={`discount-${disc.id ?? idx}`}
                                                icon={<GiftOutlined />}
                                                label={disc.discount.purpose ? i.t(`def:purposes.${disc.discount.purpose}`) : i.t('order:discountWithName', { name: disc.discount.name })}
                                                sub={discountGiftProducts.length > 0 &&
                                                    discountGiftProducts.map((p: any) => (
                                                        <div key={p.product.id} className="item__meta-vendor" style={{ marginTop: 2 }}>
                                                            {i.t('order:giftPrefix')}
                                                            <a href={`/shop/products/${p.product.id}`} target="_blank" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>{p.product.name}</a>
                                                        </div>
                                                    ))
                                                }
                                                value={<Tag color="green" style={{ marginRight: 0 }}>{i.t('order:gift')}</Tag>}
                                            />
                                        )
                                    }

                                    if (disc.promo_code) {
                                        const giftProducts = couponGiftProducts.length > 0
                                            ? couponGiftProducts
                                            : (disc.promo_code.product ? [disc.promo_code.product] : [])

                                        if (giftProducts.length === 0) {
                                            return null
                                        }

                                        return (
                                            <CostLine
                                                key={`promo-${disc.id ?? idx}`}
                                                icon={<GiftOutlined />}
                                                label={i.t('order:couponWithCode', { code: disc.promo_code.code })}
                                                sub={giftProducts.map((product: any) => (
                                                    <div key={product.id} className="item__meta-vendor" style={{ marginTop: 2 }}>
                                                        {i.t('order:giftPrefix')}
                                                        <a href={`/shop/products/${product.id}`} target="_blank" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>{product.name}</a>
                                                    </div>
                                                ))}
                                                value={<Tag color="purple" style={{ marginRight: 0 }}>{i.t('order:gift')}</Tag>}
                                            />
                                        )
                                    }

                                    return null
                                })
                            })()}

                            {__order.item.invited_id &&
                                <CostLine
                                    icon={<TeamOutlined />}
                                    label={i.t('def:referral_user')}
                                    value={<a href={`/users/${__order.item.invited_id}`} target="_blank" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>{i.t('def:jump.jump')}</a>}
                                />
                            }

                            {(__order.item.service_fee > 0 || __order.editable) &&
                                <CostLine
                                    icon={<WalletOutlined />}
                                    label={projectController.project.service_fee_name}
                                    value={__order.editable ? (
                                        <InputNumber
                                            value={__order.item?.service_fee ?? 0}
                                            onChange={e => runInAction(() => __order.item.service_fee = e)}
                                            min={__order.item?.service_fee ?? 0}
                                            size="small"
                                        />
                                    ) : __order.item.service_fee}
                                />
                            }

                            {__order.item.change_from > 0 &&
                                <CostLine
                                    icon={<RollbackOutlined />}
                                    label={i.t('def:Change from')}
                                    value={__order.item.change_from}
                                />
                            }
                        </div>

                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            width: '100%', padding: '16px 20px', borderRadius: 14,
                            background: 'linear-gradient(135deg, var(--primary-color), #9186e0)'
                        }}>
                            <span style={{ fontSize: 15, fontWeight: 600, color: '#fff', opacity: 0.9 }}>{i.t('order:total')}</span>
                            <span style={{ fontSize: 26, fontWeight: 800, color: '#fff', letterSpacing: 0.3 }}>{getPrice((__order.item.sum ?? 0).toFixed(2))}</span>
                        </div>
                    </div>
                </Col>

                <Col span={24}>
                    <div className="order__block">
                        <div className="order__block-item">
                            <div className="item__heading">{i.t('order:deliveries')}</div>
                        </div>

                        {__order.item.warehouse_id && __order.item.warehouse ? <div className="order__block-item">
                            <Row gutter={[20, 20]} align="middle">
                                <Col span={12} xl={5}>
                                    <div className="item__text item__text-bold">{i.t('def:warehouse')}</div>
                                </Col>
                                <Col span={12} xl={19}>
                                    <div className="item__text">
                                        <a href={`/settings/warehouses/${__order.item.warehouse.id}`} target="_blank">{__order.item.warehouse.name}</a>
                                    </div>
                                </Col>
                            </Row>
                        </div> : <></>}

                        <div className="order__block-item">
                            <Row gutter={[20, 20]} align="middle">
                                <Col span={12} xl={5}>
                                    <div className="item__text item__text-bold">{i.t('order:deliveryType')}</div>
                                </Col>
                                <Col span={12} xl={19}>
                                    <div className="item__text">
                                        {__order.item.delivery_type === 1 ? <>{i.t('order:deliveries')}</> : <>{i.t('order:pickup')}</>}

                                        {__order.item.delivery && __order.item.delivery !== 'default' &&
                                            <> ({__order.getDeliveryNameByType(__order.item.delivery)})</>
                                        }
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {(__order.item.delivery_method_name || __order.item.delivery_method_id) &&
                            <div className="order__block-item">
                                <Row gutter={[20, 20]} align="middle">
                                    <Col span={12} xl={5}>
                                        <div className="item__text item__text-bold">{i.t('order:tk')}</div>
                                    </Col>
                                    <Col span={12} xl={19}>
                                        <div className="item__text">
                                            {[__order.item.delivery_method_name || `#${__order.item.delivery_method_id}`, __order.item.delivery_method_description].filter(Boolean).join(' · ')}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        }

                        {(__order.item.pickup_point_name || __order.item.pickup_point_address) &&
                            <div className="order__block-item">
                                <Row gutter={[20, 20]} align="middle">
                                    <Col span={12} xl={5}>
                                        <div className="item__text item__text-bold">{i.t('order:pickupPoint')}</div>
                                    </Col>
                                    <Col span={12} xl={19}>
                                        <div className="item__text">
                                            {__order.item.pickup_point_name && <div>{__order.item.pickup_point_name}</div>}
                                            {__order.item.pickup_point_address && <div style={{ color: 'var(--text-muted-color)' }}>{__order.item.pickup_point_address}</div>}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        }

                        <div className="order__block-item">
                            <Row gutter={[20, 20]} align="middle">
                                <Col span={12} xl={5}>
                                    <div className="item__text item__text-bold">{i.t('order:dateDelivery')}</div>
                                </Col>
                                <Col span={12} xl={19}>
                                    <div className="item__text">
                                        {__order.editable ? <>
                                            <DatePicker
                                                format="DD.MM.YYYY"
                                                value={__order.item.delivery_date ? dayjs(__order.item.delivery_date) : null}
                                                placeholder={i.t('order:dateDelivery')}
                                                onChange={e => {
                                                    if (e) {
                                                        __order.changeDeliveryDate(e)
                                                    }
                                                }}
                                                minDate={dayjs()}
                                            />
                                        </> : <>{__order.item.delivery_date ? dayjs(__order.item.delivery_date).format('DD.MM.YYYY') : '—'}</>}
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {__order.editable || (__order.item.delivery_interval_start && __order.item.delivery_interval_end) ?
                            <div className="order__block-item">
                                <Row gutter={[20, 20]} align="middle">
                                    <Col span={12} xl={5}>
                                        <div className="item__text item__text-bold">{i.t('def:Delivery interval')}</div>
                                    </Col>
                                    <Col span={12} xl={19}>
                                        <div className="item__text">
                                            {__order.editable ?
                                                <Select
                                                    style={{ width: '100%', maxWidth: 300 }}
                                                    placeholder={i.t('def:Delivery interval')}
                                                    value={__order.item.delivery_interval_id ?? undefined}
                                                    onDropdownVisibleChange={(open: boolean) => {
                                                        if (open && !__order.availableIntervals) __order.getAvailableIntervals()
                                                    }}
                                                    onChange={(e: any) => __order.selectDeliveryInterval(e)}
                                                    options={__order.intervalOptions.map((el: any) => ({
                                                        value: el.id,
                                                        label: el.express
                                                            ? `${secondsToHHii(el.time_start)} - ${secondsToHHii(el.time_end)} (${i.t('def:Delivery interval EXPRESS')})`
                                                            : `${secondsToHHii(el.time_start)} - ${secondsToHHii(el.time_end)}`
                                                    }))}
                                                />
                                                : <>{secondsToHHii(__order.item.delivery_interval_start)} - {secondsToHHii(__order.item.delivery_interval_end)}</>
                                            }
                                        </div>
                                    </Col>
                                </Row>
                            </div> : <></>
                        }

                        {__order.item.delivery_interval_express_time ?
                            <div className="order__block-item">
                                <Row gutter={[20, 20]} align="middle">
                                    <Col span={12} xl={5}>
                                        <div className="item__text item__text-bold">{i.t('def:Delivery interval EXPRESS')}</div>
                                    </Col>
                                    <Col span={12} xl={19}>
                                        <div className="item__text">
                                            {i.t('def:Delivery in minutes', {
                                                minutes: __order.item.delivery_interval_express_time
                                            })}
                                        </div>
                                    </Col>
                                </Row>
                            </div> : <></>
                        }

                        {(__order.item?.tracking_number || __order.editable) &&
                            <>
                                <div className="order__block-item">
                                    <Row gutter={[20, 20]} align="middle">
                                        <Col span={24} xl={5}>
                                            <div className="item__text item__text-bold">{i.t('order:trackingNumber')}</div>
                                        </Col>
                                        <Col span={24} xl={19}>
                                            <div className="item__text">
                                                {__order.editable ? <>
                                                    <Input
                                                      value={__order.item.tracking_number}
                                                      onChange={e => runInAction(() => __order.item.tracking_number = e.target.value)}
                                                    />
                                                </> : <>{__order.item.tracking_number}</>}
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </>
                        }

                        {(__order.item?.address || __order.editable) &&
                          <>
                              <div className="order__block-item">
                                  <Row gutter={[20, 5]} align="middle">
                                      <Col span={24} xl={5}>
                                          <div className="item__text item__text-bold">{i.t('def:address')}</div>
                                      </Col>
                                      <Col span={24} xl={19}>
                                          <div className="item__text">
                                              {__order.editable ? <>
                                                  <Input
                                                    value={__order.item.address}
                                                    onChange={e => runInAction(() => __order.item.address = e.target.value)}
                                                  />
                                              </> : <>{__order.item.address}</>}
                                          </div>
                                      </Col>
                                  </Row>
                              </div>

                              {__order.item.cottage ?
                                <div className="order__block-item">
                                    <Row gutter={[20, 20]} align="middle">
                                        <Col span={12} xl={5}>
                                            <div className="item__text item__text-bold">{i.t('def:Private house')}</div>
                                        </Col>
                                        <Col span={12} xl={19}>
                                            <div className="item__text">{__order.item.cottage}</div>
                                        </Col>
                                    </Row>
                                </div> : <></>
                              }

                              {__order.item.zip &&
                                  <>
                                      <div className="order__block-item">
                                          <Row gutter={[20, 20]} align="middle">
                                              <Col span={12} xl={5}>
                                                  <div className="item__text item__text-bold">{i.t('order:Zip')}:</div>
                                              </Col>
                                              <Col span={12} xl={19}>
                                                  <div className="item__text">{__order.item.zip}</div>
                                              </Col>
                                          </Row>
                                      </div>
                                  </>
                              }

                              {__order.item.apartment &&
                                  <>
                                      <div className="order__block-item">
                                          <Row gutter={[20, 20]} align="middle">
                                              <Col span={12} xl={5}>
                                                  <div className="item__text item__text-bold">{i.t('def:Apartment')}:</div>
                                              </Col>
                                              <Col span={12} xl={19}>
                                                  <div className="item__text">{__order.item.apartment}</div>
                                              </Col>
                                          </Row>
                                      </div>
                                  </>
                              }

                              {__order.item.entrance &&
                                  <>
                                      <div className="order__block-item">
                                          <Row gutter={[20, 20]} align="middle">
                                              <Col span={12} xl={5}>
                                                  <div className="item__text item__text-bold">{i.t('def:Entrance')}:</div>
                                              </Col>
                                              <Col span={12} xl={19}>
                                                  <div className="item__text">{__order.item.entrance}</div>
                                              </Col>
                                          </Row>
                                      </div>
                                  </>
                              }

                              {__order.item.floor &&
                                  <>
                                      <div className="order__block-item">
                                          <Row gutter={[20, 20]} align="middle">
                                              <Col span={12} xl={5}>
                                                  <div className="item__text item__text-bold">{i.t('def:Floor')}:</div>
                                              </Col>
                                              <Col span={12} xl={19}>
                                                  <div className="item__text">{__order.item.floor}</div>
                                              </Col>
                                          </Row>
                                      </div>
                                  </>
                              }

                          </>
                        }

                        {((__order.item?.address_full && __order.item.address_full !== '') || __order.editable) &&
                            <div className="order__block-item">
                                <Row gutter={[20, 5]} align="middle">
                                    <Col span={24} xl={5}>
                                        <div className="item__text item__text-bold">{i.t('order:fulladdress')}</div>
                                    </Col>
                                    <Col span={24} xl={19}>
                                        <div className="item__text">
                                            {__order.editable ? <>
                                                <Input
                                                    value={__order.item.address_full}
                                                    onChange={e => runInAction(() => __order.item.address_full = e.target.value)}
                                                />
                                            </> : <>{__order.item.address_full}</>}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        }



                        {(__order.item?.latitude && __order.item?.longitude) &&
                            <div className="order__block-item">
                                <MapLocation coordinates={[__order.item.latitude, __order.item.longitude]} />
                            </div>
                        }
                    </div>
                </Col>

                <Col span={24}>
                    <div className="order__block">
                        <div className="order__block-item">
                            <div className="item__heading">
                                {i.t('order:buyer')}
                                {__order.editable &&
                                    <>
                                        {__order.item.user_id > 0 ? <Button
                                            type="dashed"
                                            danger
                                            onClick={() => __order.toggleUser()}
                                        >{i.t('def:actions.remove')}</Button> : <Button
                                            type="dashed"
                                            onClick={() => __order.toggleUser()}
                                        >{i.t('def:select')}</Button>}
                                    </>
                                }
                            </div>
                        </div>

                        {(__order.item.firstname || __order.item.lastname) &&
                            <div className="order__block-item">
                                <div className="item__text">
                                    <Row gutter={[20, 20]} align="middle">
                                        <Col span={12} xl={5}>
                                            <div className="item__text item__text-bold">{i.t('order:fio')}</div>
                                        </Col>
                                        <Col span={12} xl={19}>
                                            <div
                                                className="item__text"><a href={`/users/${__order.item.user_id}`} target="_blank">{__order.item.firstname} {__order.item.lastname}</a></div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        }

                        {__order.item.phone &&
                            <div className="order__block-item">
                                <div className="item__text">
                                    <Row gutter={[20, 20]} align="middle">
                                        <Col span={12} xl={5}>
                                            <div className="item__text item__text-bold">{i.t('order:phone')}</div>
                                        </Col>
                                        <Col span={12} xl={19}>
                                            <div className="item__text">{__order.item.phone}</div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        }

                        {__order.item.email &&
                            <div className="order__block-item">
                                <div className="item__text">
                                    <Row gutter={[20, 20]} align="middle">
                                        <Col span={12} xl={5}>
                                            <div className="item__text item__text-bold">{i.t('order:email')}</div>
                                        </Col>
                                        <Col span={12} xl={19}>
                                            <div className="item__text">{__order.item.email}</div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        }

                        {(__order.editable && __order.item.user_id) &&
                            <div className="order__block-item">
                                {__order.item.recipient ? <Button
                                    type="dashed"
                                    danger
                                    onClick={() => __order.toggleRecipient()}
                                >{i.t('def:Remove Recipient')}</Button> : <Button
                                    type="dashed"
                                    onClick={() => __order.toggleRecipient()}
                                >{i.t('def:Add Recipient')}</Button>}
                            </div>
                        }

                        {__order.item.recipient === 1 &&
                            <>
                                <div className="order__block-item">
                                    <div className="item__heading">{i.t('order:recipient')}</div>
                                </div>

                                <div className="order__block-item">
                                    <div className="item__text">
                                        <Row gutter={[20, 20]} align="middle">
                                            <Col span={12} xl={5}>
                                                <div className="item__text item__text-bold">{i.t('order:fio')}</div>
                                            </Col>
                                            <Col span={12} xl={19}>
                                                <div className="item__text">
                                                    {__order.editable ? <>
                                                        <Input
                                                            value={__order.item.recipient_name}
                                                            onChange={e => runInAction(() => __order.item.recipient_name = e.target.value)}
                                                        />
                                                    </> : <>{__order.item.recipient_name}</>}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </div>

                                <div className="order__block-item">
                                    <div className="item__text">
                                        <Row gutter={[20, 20]} align="middle">
                                            <Col span={12} xl={5}>
                                                <div className="item__text item__text-bold">
                                                    {i.t('order:phone')}
                                                </div>
                                            </Col>
                                            <Col span={12} xl={19}>
                                                <div className="item__text">
                                                    {__order.editable ? <>
                                                        <Input
                                                            value={__order.item.recipient_phone}
                                                            onChange={e => runInAction(() => __order.item.recipient_phone = e.target.value)}
                                                        />
                                                    </> : <>{__order.item.recipient_phone}</>}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            </>
                        }
                    </div>
                </Col>

                {__order.item.fields && __order.item.fields.length > 0 ? <>
                    <Col span={24}>
                        <div className="order__block">
                            <div className="order__block-item">
                                <div className="item__heading">{i.t('order:additionalFields')}</div>
                                <Col span={24} className="detail_element">
                                    <Row gutter={[15, 15]}>
                                        <AdditionalFields fields={__order.item.fields} />
                                    </Row>
                                </Col>
                            </div>
                        </div>
                    </Col>

                </>: <></>}

                {__order.item.company ?
                  <Col span={24}>
                      <div className="order__block">
                          <div className="order__block-item">
                              <div className="item__heading">
                                  {i.t('def:Company')}
                              </div>
                              <div className="order__block-item">
                                  <Col span={24}>
                                      <Row className="company_search" gutter={[10, 10]}>
                                          <Col span={24}>
                                              {__order.item.company.type === 2 ? <Tag>{i.t('def:Legal entity')}</Tag> : <Tag>{i.t('def:Individual entrepreneur')}</Tag>}
                                              <Tag color={__order.item.company.status ? "success" : "error"}>{__order.item.company.status ? i.t('order:companyActive') : i.t('order:companyInactive')}</Tag>
                                          </Col>
                                          <Col span={24}>
                                              {__order.item.company.type === 2
                                                ? <p>{__order.item.company.name}</p>
                                                : <p>{__order.item.company.fio}</p>
                                              }
                                          </Col>
                                          <Col span={24}>
                                              <p>{i.t('order:innOgrn', { inn: __order.item.company.inn, ogrn: __order.item.company.ogrn })}</p>
                                          </Col>
                                          {__order.item.company.kpp &&
                                            <Col span={24}>
                                                <p>{i.t('order:kppLine', { kpp: __order.item.company.kpp })}</p>
                                            </Col>
                                          }
                                          {__order.item.company.address &&
                                            <Col span={24}>
                                                <p>{i.t('order:legalAddressLine', { address: __order.item.company.address })}</p>
                                            </Col>
                                          }
                                      </Row>
                                  </Col>
                              </div>
                          </div>
                      </div>
                  </Col> : <></>
                }


                <Col span={24}>
                    <div className="order__block">
                        <div className="order__block-item">
                            <div className="item__heading">
                                <div>{i.t('order:listOrder')}</div>
                                {__order.item?.number_of_seats
                                  ? <p>{i.t('order:seatsCount', { count: __order.item?.number_of_seats })}</p>
                                  : <></>}
                                {__order.editable &&
                                  <Button
                                    type="dashed"
                                    onClick={() => __order.setSelect('products')}
                                  >{i.t('def:actions.add')}</Button>
                                }
                            </div>
                        </div>


                        <div className="order__block-item">
                            <OrderProducts
                                items={__order.item.products}
                                editable={__order.editable}
                                setQuantity={(e: any, e1: any, e2: any, e3: any) => __order.setQuantity(e, e1, e2, e3)}
                                deleteProduct={(e: any) => __order.deleteProduct(e, 'products')}
                            />
                        </div>

                        {__order.item.free_products.length > 0 || __order.editable
                          ?
                            <>
                                <Divider />
                                <div className="order__block-item">
                                    <div className="item__heading">
                                        <div>{i.t('order:free_products')}</div>
                                        {__order.editable &&
                                          <Button
                                            type="dashed"
                                            onClick={() => __order.setSelect('free_products')}
                                          >{i.t('def:actions.add')}</Button>
                                        }
                                    </div>
                                </div>
                            </>
                        : <></>}


                        {__order.item.free_products.length > 0
                          ?
                              <div className="order__block-item">
                                  <OrderProducts
                                    items={__order.item.free_products}
                                    editable={__order.editable}
                                    setQuantity={(e: any, e1: any, e2: any, e3: any) => __order.setQuantity(e, e1, e2, e3)}
                                    deleteProduct={(e: any) => __order.deleteProduct(e, 'free_products')}
                                  />
                              </div>
                          : <></>}
                    </div>
                </Col>

                {__order.item?.cutlery || __order.item.comment || __order.item.check_number || __order.editable
                    ?   <Col span={24}>
                      <div className="order__block">
                          <div className="order__block-item">
                              <div className="item__heading">{i.t('order:additionally')}</div>
                          </div>

                          {((__order.item.comment && __order.item.comment !== '') || __order.editable) &&
                            <div className="order__block-item">
                                <div className="item__text">
                                    <Row gutter={[10, 10]}>
                                        <Col span={24}>
                                            <div className="item__text item__text-bold">{i.t('order:comment')}</div>
                                        </Col>
                                        <Col span={24}>
                                            <div className="item__text">
                                                {__order.editable ? <>
                                                    <Input
                                                      value={__order.item.comment}
                                                      onChange={e => runInAction(() => __order.item.comment = e.target.value)}
                                                    />
                                                </> : <>{__order.item.comment}</>}
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                          }

                          {__order.item?.check_number ?
                            <>
                                <div className="order__block-item">
                                    <div className="item__text">
                                        <Row gutter={[10, 10]}>
                                            <Col span={24}>
                                                <div className="item__text item__text-bold">{i.t('def:Order check number')}</div>
                                            </Col>
                                            <Col span={24}>
                                                <div className="item__text">
                                                    {__order.item.check_number}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            </>
                            : <></>}

                          {(__order.item?.cutlery && __order.item.cutlery >= 0 || __order.editable) &&
                            <div className="order__block-item">
                                <div className="item__text">
                                    <Row gutter={[10, 10]}>
                                        <Col span={24}>
                                            <div className="item__text item__text-bold">{i.t('order:cutlery')}</div>
                                        </Col>
                                        <Col span={24}>
                                            <div className="item__text">
                                                {__order.editable ? <>
                                                    <InputNumber
                                                      value={__order.item?.cutlery ?? 0}
                                                      onChange={e => runInAction(() => __order.item.cutlery = e)}
                                                    />
                                                </> : <>{__order.item.cutlery}</>}
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                          }
                      </div>
                  </Col>
                    : <></>
                }

                {(__order.item.system_info && __order.item.system_info !== '') &&
                    <Col span={24}>
                        <div className="order__block">
                            <div className="order__block-item">
                                <div className="item__heading">{i.t('order:tech')}</div>
                                <div className="item__text">{__order.item.system_info}</div>
                            </div>
                        </div>
                    </Col>
                }

                {(__order.item.source && __order.item.source !== '') &&
                  <Col span={24}>
                      <div className="order__block">
                          <div className="order__block-item">
                              <div className="item__heading">{i.t('order:source')}</div>
                              <div className="item__text">{__order.item.source}</div>
                          </div>
                      </div>
                  </Col>
                }

                {(__order.item.history && __order.item.history.length > 0) &&
                  <Col span={24}>
                      <div className="order__block">
                          <div className="order__block-item">
                              <div className="item__heading">{i.t('def:History changed')}</div>

                              <Row gutter={[20, 20]}>
                                  {__order.item.history.map((el: any) => <>
                                    <Col span={24}><p className="order_history">{dayjs(el.created_at).format('DD.MM.YYYY HH:mm')} - {el.author} {i.t('order:historyChangedField')} {el.field}. {i.t('order:historyValueBefore')} <strong>{el.before}</strong> {i.t('order:historyValueAfter')} <strong>{el.after}</strong></p></Col>
                                  </>)}
                              </Row>
                          </div>
                      </div>
                  </Col>
                }

                {(__order.item.applicationsLogs && __order.item.applicationsLogs.length > 0) ?
                  <Col span={24}>
                      <div className="order__block">
                          <div className="order__block-item">
                              <div className="item__heading">{i.t('order:thirdPartyAppLogs')}</div>
                          </div>

                          <Row gutter={[20, 20]}>
                              <OrderLogs logs={__order.item.applicationsLogs} />
                          </Row>
                      </div>
                    </Col> : <></>
                }
            </Row>

            <Modal
                title={__order.select.type === 'free_products' ? i.t('order:addFreeProductTitle') : i.t('order:addProductTitle')}
                open={__order.select.open}
                width={1100}
                footer={null}
                onCancel={() => __order.setSelect()}
                maskClosable={false}
                // Каждое открытие загружает список заново: выбранные в строках свойства не переносятся
                destroyOnClose
            >
                <ProductTable
                    table={productTable}
                    picker={{
                        add: true,
                        // Добавленные — в том списке, куда добавляем: товары или подарки
                        selectedIds: (__order.item[__order.select.type] ?? []).map((el: any) => Number(el.product_id)),
                        onToggle: (product: any) => __order.addProduct(product),
                        // У товара с вариациями добавляются только вариации
                        canPick: (product: any) => !product.view_variations,
                        renderExtra: (product: any) => !product.is_variant && Array.isArray(product.props) && product.props.length > 0
                            ? <ProductPropsSelect product={product} />
                            : null,
                    }}
                />
            </Modal>

            <Modal
                open={__order.usersModal.open}
                width={1000}
                footer={null}
                onCancel={() => __order.setUsers()}
                maskClosable={false}
            >
                <Row gutter={[20, 20]}>
                    <Col span={6}>
                        <label>{i.t('users:name')}</label>
                        <Input
                          value={__order.usersFilters.name ?? ''}
                          onChange={(e: any) => __order.setUserFilter('name', e.target.value)}
                        />
                    </Col>
                    <Col span={6}>
                        <label>{i.t('users:phone')}</label>
                        <Input
                          value={__order.usersFilters.phone ?? ''}
                          onChange={(e: any) => __order.setUserFilter('phone', e.target.value)}
                        />
                    </Col>
                    <Col span={6}>
                        <label>{i.t('users:email')}</label>
                        <Input
                          value={__order.usersFilters.email ?? ''}
                          onChange={(e: any) => __order.setUserFilter('email', e.target.value)}
                        />
                    </Col>
                </Row>

                <br/>

                <Table
                    rowClassName={() => 'editable-row'}
                    columns={usersColumns}
                    dataSource={__order.users}
                    rowKey="id"
                    pagination={{
                        position: ['bottomLeft'],
                        hideOnSinglePage: true,
                        current: __order.pagination.page,
                        pageSize: __order.pagination.limit,
                        total: __order.pagination.total,
                        showSizeChanger: false,
                        onChange: (e) => __order.setUsersPage(e)
                    }}
                />
            </Modal>

            <Modal
              title={i.t('order:paymentConfirmationTitle')}
              open={__order.holding_modal.open}
              onOk={() => __order.saveHolding(__order.item.id)}
              onCancel={() => __order.closeModalHolding()}
            >
                <p>{i.t('def:holding_title')}</p>
                <InputNumber
                  value={__order.holding_modal.sum}
                  style={{ width: '100%' }}
                  onChange={onChangeModalSum}
                  decimalSeparator=","
                  min={1}
                  max={__order.item.sum}
                  disabled={storeLocal.get('role') === 1}
                />
            </Modal>
        </>
    )
})

const OrderLogs = ({ logs }: any) => {

    const columns = [
        {
            title: i.t('order:logDate'),
            key: 'timestamp',
            dataIndex: 'timestamp',
            render: (value: string) => value ? dayjs(value).format('DD.MM.YYYY HH:mm:ss') : '—'
        },
        {
            title: i.t('order:application'),
            key: 'applicationName',
            dataIndex: 'applicationName',
        },
        {
            title: i.t('order:message'),
            key: 'message',
            render: (record: any) => <>
                <b>{record.label}</b>
                {record.description ? <p>{record.description}</p> : <></>}
            </>
        },
        {
            title: i.t('order:additionalInfo'),
            dataIndex: 'value',
            key: 'value',
            ellipsis: true,
            render: (value: any) => {
                const text = getLogValueText(value);

                if (!text) return '—';

                return (
                    <Tooltip title={i.t('order:clickToCopy')}>
                        <span
                            className="order-log-value"
                            onClick={() => {
                                navigator.clipboard.writeText(text);
                                message.success(i.t('order:copied'));
                            }}
                        >
                          {text}
                        </span>
                    </Tooltip>
                );
            },
        },
    ]

    return (
      <Table
        columns={columns}
        dataSource={logs}
        pagination={false}
        tableLayout="fixed"
      />
    )
}
